import React, { useState, useRef, useEffect, useCallback } from "react";
import ChatBotHeader from "./ChatBotHeader";
import ChatBotMessages from "./ChatBotMessages";
import ChatBotInput from "./ChatBotInput";
import { v4 as uuidv4 } from "uuid";
import useSoundEffects from "./useSoundEffects";

const DEFAULT_API_URL = process.env.NODE_ENV === "production" ? window.location.origin : "http://localhost:8000";
const API_BASE_URL = (process.env.REACT_APP_API_URL || DEFAULT_API_URL).replace(/\/$/, "");

const IMPORTANT_TERMS = [
  "myBOQ", "MyBOQ", "SSR", "Schedule of Rates", "PWD", "Public Works Department",
  "Maharashtra", "Estimate", "Estimation", "BOQ", "Bill of Quantities",
  "Schedule B", "Abstract", "Rate Analysis", "Prime Yearly", "Prime Monthly",
  "Flex", "Unlimited Lifetime", "Credits", "Credit", "SiliconMount", "CEIIT",
  "AI Pilot", "AI Roadmap", "Government-ready", "SSR-compliant",
  "lead distances", "floor/height logic", "Draft Tender Papers", "Measurement Sheets",
  "Training Bundle", "DPIIT", "SSR norms", "Schedule of Rates", "Rate Analysis Reports",
  "Royalty Calculation", "Quality Test Reports", "Auto-calculated", "Auto-calculates",
  "Multi-revision", "Smart Work Duplication", "Cloud-based", "On-screen preview",
  "One-click PDF", "Professional PDFs", "Submission-ready", "Government-Ready",
  "myBOQ AI", "BOQ AI"
];

function boldImportantTerms(text) {
  let result = text;
  const sortedTerms = [...IMPORTANT_TERMS].sort((a, b) => b.length - a.length);
  
  for (const term of sortedTerms) {
    const regex = new RegExp(`\\b(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi');
    result = result.replace(regex, '**$1**');
  }
  
  return result;
}

const INITIAL_MESSAGES = {
  english: {
    id: "initial-greeting",
    content: "Hello! I'm **BOQ AI** — your MyBOQ website assistant. 👋\n\nI can help you with:\n\n- **MyBOQ features** — Estimation engine, PDF reports, revisions\n- **Pricing plans** — Flex, Prime Monthly, Prime Yearly, Unlimited Lifetime\n- **SSR & PWD compliance** — Maharashtra PWD rates, Schedule B, Abstract sheets\n- **AI Roadmap** — Upcoming AI features and pilot program\n- **Getting started** — Free signup, dashboard walkthrough\n\nWhat would you like to know?",
    isUser: false,
    timestamp: new Date(),
    sources: [],
    followUps: [],
  },
  marathi: {
    id: "initial-greeting",
    content: "नमस्कार! मी **BOQ AI** — तुमचा MyBOQ वेबसाइट सहायक आहे. 👋\n\nमी तुम्हाला खालील विषयी मदत करू शकते:\n\n- **MyBOQ वैशिष्ट्ये** — अंदाज इंजिन, PDF अहवाल, पुनरावृत्ती\n- **किंमत योजना** — फ्लेक्स, प्राइम मासिक, प्राइम वार्षिक, अनलिमिटेड लाइफटाइम\n- **SSR आणि PWD कम्प्लायन्स** — महाराष्ट्र PWD दर, स्केड्यूल B, सारांश शीट\n- **AI रोडमॅप** — आगामी AI वैशिष्ट्ये आणि पायलट प्रोग्रॅम\n- **सुरुवात करणे** — मोफत साइनअप, डॅशबोर्ड वॉकथ्रू\n\nतुम्हाला काय जाणून घ्यायचे आहे?",
    isUser: false,
    timestamp: new Date(),
    sources: [],
    followUps: [],
  },
};

const ChatBotWindow = ({
  isOpen,
  onClose,
  onMinimize,
  onNewMessage,
  apiUrl,
  onOpenContactModal,
  initialLanguage,
  onFullscreenChange,
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState(() => 
    initialLanguage || window.localStorage.getItem("myboq-chat-language") || "english"
  );
  
  const [messages, setMessages] = useState(() => [INITIAL_MESSAGES[selectedLanguage] || INITIAL_MESSAGES.english]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [sessionId] = useState(() => {
    const storedSessionId = window.localStorage.getItem("myboq-chat-session-id");
    const nextSessionId = storedSessionId || uuidv4();
    window.localStorage.setItem("myboq-chat-session-id", nextSessionId);
    return nextSessionId;
  });
  
  // Persist language selection
  useEffect(() => {
    window.localStorage.setItem("myboq-chat-language", selectedLanguage);
  }, [selectedLanguage]);
  
  // Update initial message when language changes (only if no real messages yet)
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 1 && prev[0].id === "initial-greeting") {
        return [INITIAL_MESSAGES[selectedLanguage] || INITIAL_MESSAGES.english];
      }
      return prev;
    });
  }, [selectedLanguage]);
  
  const handleLanguageChange = useCallback((lang) => {
    setSelectedLanguage(lang);
  }, []);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const { playSend, playAttach, playReceive, playError } = useSoundEffects();

  const uploadDocument = useCallback(async (file) => {
    const endpoint = apiUrl || API_BASE_URL;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("session_id", sessionId);

    try {
      const response = await fetch(`${endpoint}/api/upload-document`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Upload failed");
      }

      const data = await response.json();
      console.log("Document uploaded:", data);
      return data;
    } catch (err) {
      console.error("Document upload error:", err);
      playError();
      throw err;
    }
  }, [apiUrl, sessionId, playError]);

  const fetchChatHistory = useCallback(async (sessionIdParam) => {
    const endpoint = apiUrl || API_BASE_URL;
    try {
      const response = await fetch(`${endpoint}/api/chat/history/${sessionIdParam}?limit=50`);
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`Failed to fetch history: ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      console.error("Fetch chat history error:", err);
      return null;
    }
  }, [apiUrl]);

  // Load existing chat history on mount
  useEffect(() => {
    const loadHistory = async () => {
      const historyData = await fetchChatHistory(sessionId);
      if (historyData && historyData.history && historyData.history.length > 0) {
        const formattedMessages = historyData.history.map((msg, index) => ({
          id: `history-${index}`,
          content: msg.content,
          isUser: msg.role === "user",
          timestamp: msg.created_at ? new Date(msg.created_at) : new Date(),
          sources: [],
          followUps: msg.follow_up_question ? [msg.follow_up_question] : [],
        }));
        setMessages(formattedMessages);
      }
    };
    loadHistory();
  }, [sessionId, fetchChatHistory]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 150);
    }
  }, [isOpen]);

  const handleSendMessage = useCallback(
    async (content, files = []) => {
      if (!content.trim() && files.length === 0) return;

      playSend();

      const userMessage = {
        id: uuidv4(),
        content: content.trim(),
        isUser: true,
        timestamp: new Date(),
        files: files.map((f) => ({ name: f.name, type: f.type, size: f.size })),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      const endpoint = apiUrl || API_BASE_URL;
      const botMessageId = uuidv4();

      setMessages((prev) => [...prev, {
        id: botMessageId,
        content: "",
        isUser: false,
        timestamp: new Date(),
        sources: [],
        followUps: [],
      }]);

      const updateBotMessage = (updates) => {
        setMessages((prev) => prev.map((message) =>
          message.id === botMessageId ? { ...message, ...updates } : message
        ));
      };

      try {
        let streamedAnswer = "";
        let finalData = null;
        let streamSucceeded = false;

        // Try ultra-fast token streaming first
        try {
          const streamResponse = await fetch(`${endpoint}/api/chat/stream`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              question: content.trim(),
              session_id: sessionId,
              language: selectedLanguage,
              site_url: window.location.href,
            }),
          });

          if (streamResponse.ok && streamResponse.body) {
            const reader = streamResponse.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let buffer = "";

            while (true) {
              const { value, done } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split("\n");
              buffer = lines.pop(); // keep remainder

              for (const line of lines) {
                const trimmed = line.trim();
                if (trimmed.startsWith("data: ")) {
                  try {
                    const payload = JSON.parse(trimmed.slice(6));
                    if (payload.type === "token" && payload.token) {
                      streamedAnswer += payload.token;
                      updateBotMessage({
                        content: boldImportantTerms(streamedAnswer),
                      });
                      streamSucceeded = true;
                    } else if (payload.type === "content" && payload.content) {
                      streamedAnswer = payload.content;
                      updateBotMessage({
                        content: boldImportantTerms(streamedAnswer),
                      });
                      streamSucceeded = true;
                    } else if (payload.type === "done") {
                      finalData = payload;
                    }
                  } catch (e) {
                    // Ignore partial chunk parse
                  }
                }
              }
            }
          }
        } catch (streamErr) {
          console.warn("Stream unavailable, falling back to REST endpoint:", streamErr);
        }

        if (streamSucceeded) {
          const finalAnswer = (finalData && finalData.answer) ? finalData.answer : streamedAnswer;
          updateBotMessage({
            content: boldImportantTerms(finalAnswer || "I couldn't generate an answer for that."),
            followUps: (finalData && finalData.follow_up_questions) || [],
            sources: (finalData && finalData.sources) || [],
          });
          onNewMessage(false);
          playReceive();
          if (finalData && finalData.action === "open_contact_form" && onOpenContactModal) {
            onOpenContactModal();
          }
        } else {
          // Fallback to standard endpoint
          const response = await fetch(`${endpoint}/api/chat/message`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              question: content.trim(),
              session_id: sessionId,
              language: selectedLanguage,
              site_url: window.location.href,
            }),
          });

          if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();
          updateBotMessage({
            content: boldImportantTerms(data.answer || "I couldn't generate an answer for that."),
            followUps: data.follow_up_questions || [],
            sources: data.sources || [],
          });

          onNewMessage(false);
          playReceive();
          if (data.action === "open_contact_form" && onOpenContactModal) onOpenContactModal();
        }
      } catch (err) {
        console.error("Chat backend error:", err);
        playError();
        updateBotMessage({
          content: "The MyBOQ assistant is temporarily unavailable. Please try again shortly.",
          isError: true,
          sources: [],
          followUps: [],
        });
      } finally {
        setIsLoading(false);
        setAttachedFiles([]);
      }
      },
    [apiUrl, sessionId, onNewMessage, onOpenContactModal, playSend, playReceive, playError, selectedLanguage]
  );

  const handleAttachFiles = useCallback(async (files) => {
    const validFiles = Array.from(files).filter(
      (f) => f.size <= 50 * 1024 * 1024
    );
    if (validFiles.length > 0) {
      playAttach();
    }
    
    // Add files to UI immediately
    setAttachedFiles((prev) => [...prev, ...validFiles].slice(0, 5));
    
    // Upload each file to backend for RAG
    for (const file of validFiles) {
      try {
        await uploadDocument(file);
        // Track uploaded document
        setUploadedDocs((prev) => [...prev, { name: file.name, type: file.type, size: file.size }]);
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
      }
    }
  }, [playAttach, uploadDocument]);

  const handleRemoveFile = useCallback((index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleClearDocument = useCallback(() => {
    // Placeholder for document context clearing
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((current) => {
      const next = !current;
      if (onFullscreenChange) onFullscreenChange(next);
      return next;
    });
  }, [onFullscreenChange]);

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsFullscreen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen]);

  const handleResetChat = useCallback(() => {
    const nextSessionId = uuidv4();
    window.localStorage.setItem("myboq-chat-session-id", nextSessionId);
    setMessages([
      {
        ...(INITIAL_MESSAGES[selectedLanguage] || INITIAL_MESSAGES.english),
        id: uuidv4(),
        timestamp: new Date(),
      },
    ]);
    setAttachedFiles([]);
    setUploadedDocs([]);
  }, [selectedLanguage]);

  return (
    <div
      className={`fixed flex flex-col overflow-hidden bg-white ${
        isFullscreen
          ? "inset-0 w-screen h-screen z-[9999] rounded-none border-0 shadow-none"
          : "bottom-[150px] sm:bottom-[154px] right-3 sm:right-6 w-[calc(100vw-1.5rem)] sm:w-[410px] md:w-[440px] h-[580px] max-h-[calc(100vh-170px)] z-[1000] rounded-2xl shadow-2xl shadow-slate-900/25 border border-slate-200/90"
      } ${
        isOpen ? "animate-slide-up" : "hidden"
      }`}
      role="dialog"
      aria-label="BOQ AI Chat Assistant"
      aria-modal="true"
    >
      <ChatBotHeader
        onClose={onClose}
        onMinimize={onMinimize}
        onResetChat={handleResetChat}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        documentContext={null}
        onClearDocument={handleClearDocument}
        selectedLanguage={selectedLanguage}
        onLanguageChange={handleLanguageChange}
      />

      <ChatBotMessages
        messages={messages}
        isLoading={isLoading}
        messagesEndRef={messagesEndRef}
        onSelectPrompt={(promptText) => handleSendMessage(promptText)}
        onSelectFollowUp={(prompt) => handleSendMessage(prompt)}
        selectedLanguage={selectedLanguage}
      />

      <ChatBotInput
        ref={inputRef}
        onSendMessage={handleSendMessage}
        onAttachFiles={handleAttachFiles}
        attachedFiles={attachedFiles}
        uploadedDocs={uploadedDocs}
        onRemoveFile={handleRemoveFile}
        isLoading={isLoading}
        disabled={!isOpen}
        selectedLanguage={selectedLanguage}
      />


    </div>
  );
};

export default ChatBotWindow;